import React, { useReducer } from 'react'
import {
  Company,
  JobsLocationsDocument,
  JobsLocationsQuery,
  JobsLocationsQueryVariables,
  LocaleEnum,
  Metadata,
  MetadataDocument,
  MetadataQuery,
  MetadataQueryVariables,
  PageEnum,
  SearchCompaniesListDocument,
  SearchCompaniesListQuery,
  SearchCompaniesListQueryVariables,
} from 'generated/apolloComponents'
import { addApolloState, initializeApollo } from '@/api/apollo'
import CompanyProfilesTemplate from '@/components/templates/CompanyProfiles'
import {
  CompaniesContext,
  companiesReducer,
  convertProfilesStateFiltersToQueryFilters,
  initialCompaniesState,
} from '@/context/companiesContext'
import { convertSlugToState } from '@/helpers/filters'
import CustomHead from '@/components/molecules/CustomHead'
import { ScrollRestorer } from '@/components/atoms/ScrollRestorer'

interface IProps {
  locations: string[]
  companies: Company[]
  totalCount: number
  slugState: any
  metaData: Metadata
}

const CompaniesProfiles = ({
  locations,
  companies,
  totalCount,
  slugState,
  metaData,
}: IProps) => {
  const [state, dispatch] = useReducer(companiesReducer, initialCompaniesState)

  return (
    <>
      <CustomHead metaData={metaData} />
      <CompaniesContext.Provider value={{ state, dispatch }}>
        <ScrollRestorer />
        <CompanyProfilesTemplate
          locations={locations}
          companies={companies}
          totalCount={totalCount}
          metaData={metaData}
          slugState={slugState}
        />
      </CompaniesContext.Provider>
    </>
  )
}

export const getServerSideProps = async (context: any) => {
  const page: number = 1
  const perPage: number = 20
  const apolloClient = initializeApollo({ ctx: context })

  const stateBySlug = convertSlugToState(context.query.slug, { page, perPage })
  const queryByState = convertProfilesStateFiltersToQueryFilters(
    stateBySlug.filters
  )
  const queries: any = []
  const jobsLocationsQuery = apolloClient.query<
    JobsLocationsQuery,
    JobsLocationsQueryVariables
  >({
    query: JobsLocationsDocument,
    variables: {
      language: LocaleEnum[context.locale as 'pl' | 'en'],
    },
  })

  queries.push(jobsLocationsQuery)

  const companiesQuery = apolloClient.query<
    SearchCompaniesListQuery,
    SearchCompaniesListQueryVariables
  >({
    query: SearchCompaniesListDocument,
    variables: {
      page: page,
      perPage: perPage,
      filters: queryByState,
      language: LocaleEnum[context.locale as 'pl' | 'en'],
    },
  })

  queries.push(companiesQuery)

  const metaDataQuery = apolloClient.query<
    MetadataQuery,
    MetadataQueryVariables
  >({
    query: MetadataDocument,
    variables: {
      pageType: PageEnum.CompanyList,
      language: LocaleEnum[context.locale as 'pl' | 'en'],
      companyFilters: queryByState,
    },
  })
  queries.push(metaDataQuery)
  const [jobsLocationsData, companiesData, metaData]: any = await Promise.all(
    queries
  )

  return addApolloState(apolloClient, {
    props: {
      metaData: metaData.data.metadata,
      slugState: stateBySlug,
      totalCount: companiesData.data.searchCompanies.totalCount,
      companies: companiesData.data.searchCompanies.nodes,
      locations: jobsLocationsData.data.jobsLocations,
    },
  })
}

export default CompaniesProfiles
